import type { IShellDescriptor, IShellWaitingHandOver, ShellCapabilityType } from '@featit/shared';

/**
 * Am I running inside the iPhone and iPad app (T-0320, ADR-118)?
 *
 * A leaf, like `desktop/desktopDetection.ts`, and for the same reason: `ApiService` asks the vault
 * whether to mirror a session, and the vault must not import anything that imports `ApiService`.
 *
 * ⛔ **The one place the question is answered.** It reads only `window.featitShell`, the descriptor
 * the shell injects at document start. Never the user agent: the `FEATitShell/…` token is for server
 * logs, and a string any client can send is not how the web decides to hide its own navigation.
 * `shellDetection.guard.test.ts` fails on any other module that reads the descriptor or the token.
 */
export function isShellRuntime(): boolean {
  return shellDescriptor() !== null;
}

/**
 * F-0578 WAIT-4 (T-0667): the waiting study the shell has been drawing since its first frame, and when
 * that was, or `null` outside the app or from a shell that hands nothing over. The wait that draws
 * first chooses; this page's waits take it (`waitingSession.ts`).
 */
/** The shell's clock and the page's are one device's; a few seconds of difference is not a future. */
const HAND_OVER_FUTURE_SLACK_MS = 5000;

export function shellWaitingHandOver(): IShellWaitingHandOver | null {
  const waiting = shellDescriptor()?.waiting;
  if (!waiting || typeof waiting.study !== 'string' || !Number.isFinite(waiting.startedAtMs)) {
    return null;
  }
  // A start in the future is not a clock the shell could have run (a skewed device clock, or a
  // malformed value): the web picks its own rather than adopt it.
  if (waiting.startedAtMs > Date.now() + HAND_OVER_FUTURE_SLACK_MS) return null;
  return waiting;
}

/**
 * F-0519: does the shell draw the navigation — its own tab bar and navigation bar — in place of the
 * web's sidebar, header, drawer and bottom bar? The iPhone app does. The iPad app lists
 * `webNavigation`: it hosts one full-window web view, as the desktop app does, and the web keeps all
 * of its own navigation, laid out by width as in a browser. An iPad build older than F-0519 does not
 * list it, and still draws its bar.
 *
 * ⛔ The predicate for hiding the web's own navigation. `isShellRuntime()` answers a different
 * question — "is there a native shell at all" — and hid the iPad's sidebar under it (F-0519).
 */
export function shellDrawsNavigation(): boolean {
  return isShellRuntime() && !hasShellCapability('webNavigation');
}

/**
 * I-1368 (IP-3, the captain's ruling 2026-10-09): is this the app — the iPhone's or the iPad's — whose
 * main bar is the app's? The app's bar is the stored order on the app's slots (Decisions, Activity,
 * Items, the recent slot, More), with Create as the floating ＋ of a tab root. A browser's bar is
 * the web's own (AC-17.5: three slots, Home by default, Create in the middle). The iPad app draws its
 * bar in the web, as a browser does, but it is the same app and Jay's order applies to it.
 */
export function shellDrawsAppBar(): boolean {
  return shellDrawsNavigation() || shellLeavesNavigationToWeb();
}

/**
 * I-1304 (IL-17): does the shell leave the navigation to the web — the iPad app, which lists
 * `webNavigation`? Then nothing native goes back, and the web draws its own Back and Forward and
 * lists ⌘[ and ⌘]. A browser has its own, and the iPhone app's bar has its back.
 */
export function shellLeavesNavigationToWeb(): boolean {
  return isShellRuntime() && hasShellCapability('webNavigation');
}

/**
 * I-1712 (AR-7, App Review Guideline 3.1.1): may this surface offer a purchase, show a price or a
 * plan link, or point to buying elsewhere? Not in the iOS app — the iPhone's or the iPad's — which
 * may sell only through In-App Purchase. Yes in a browser (iPad Safari too), the desktop app and the
 * Android shell.
 *
 * ⛔ The one predicate for every billing surface: a plan section is left out, a control is not
 * drawn, a sentence states the limit and no more. A platform's rule, not a person's permission, so
 * nothing is drawn disabled in its place. `purchaseSurfaces.guard.test.ts` fails on a billing
 * surface that does not ask it, and on any other module that reads the shell's platform.
 */
export function canOfferPurchases(): boolean {
  return shellDescriptor()?.platform !== 'ios';
}

/**
 * F-0683: is this the Android shell? A machine grant signed with the phone's key names the app that
 * holds it (`client`): the Android shell's, or else the iOS app's. Asked here, so the shell's platform
 * is read in this one module (`purchaseSurfaces.guard.test.ts`).
 */
export function shellRunsOnAndroid(): boolean {
  return shellDescriptor()?.platform === 'android';
}

/** The shell's descriptor, or `null` in a browser. A malformed one counts as no shell at all. */
export function shellDescriptor(): IShellDescriptor | null {
  if (typeof window === 'undefined') return null;
  const descriptor = window.featitShell;
  if (
    typeof descriptor !== 'object' ||
    descriptor === null ||
    typeof descriptor.version !== 'string' ||
    !Array.isArray(descriptor.capabilities)
  ) {
    return null;
  }
  return descriptor;
}

/**
 * Whether the shell can do `capability`. A native action is offered only when this says yes, so an
 * older app never shows a button it cannot honour.
 */
export function hasShellCapability(capability: ShellCapabilityType): boolean {
  return shellDescriptor()?.capabilities.includes(capability) ?? false;
}

/**
 * Where the web installs the function the shell calls with each event. On the descriptor itself, so
 * the shell has one global to find.
 */
export function installShellDispatch(dispatch: (event: unknown) => void): boolean {
  const descriptor = shellDescriptor();
  if (!descriptor) return false;
  (descriptor as NonNullable<Window['featitShell']>).dispatch = dispatch;
  return true;
}
